import { Button, Form, Select } from "antd";
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useLocation, useParams } from "react-router-dom";
import testSvg from "@assets/icons/test.svg";

import RegenerateIcon from "@assets/icons/RegenerateIcon";
import down from "@assets/images/down.svg";
import GoBack from "@commonComponents/go-back";
import SaveQuit from "@commonComponents/save-quit";
import { useStore } from "@stores/root-store";

import style from "./style.module.scss";
import ReportModal from "@components/modal/report-modal";
import { CheckOutlined, DownOutlined } from "@ant-design/icons";

export interface FooterButtonProps {
  handleSubmit?: any;
  handlegoback?: any;
  handleSaveAndQuit?: (data?: any) => void;
  handleContinueInPlayground?: () => void;
  handleGoNext?: any;
  handleRegenrate?: any;
  isResult?: boolean;
  isSubmitting?: boolean;
  isPreparingPlayground?: boolean;
  isSaving?: boolean;
  isSubmit?: boolean;
  isLoading?: boolean;
  isLoadingRegenrate?: boolean;
  isLoadingSubmit?: boolean;
  form?: any;
  handleQuickNext?: any;
  customStyle?: any;
  setGenerateText?: any;
  withRegenerate?: boolean;
  isTooltip?: boolean;
  setIsSubmitting?: any;
  disableSubmit?: boolean;
}

const FooterButton = ({
  handleSubmit,
  handlegoback,
  isSubmitting,
  handleSaveAndQuit,
  handleContinueInPlayground,
  handleRegenrate,
  handleGoNext,
  isResult = false,
  isPreparingPlayground,
  isLoading = false,
  isSaving = false,
  isLoadingRegenrate,
  isLoadingSubmit,
  form,
  handleQuickNext,
  customStyle,
  setGenerateText,
  isSubmit,
  withRegenerate,
  isTooltip,
  setIsSubmitting,
  disableSubmit,
}: FooterButtonProps) => {
  const { pathname } = useLocation();
  const [isTooltipShow, setIsTooltipShow] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { type } = useParams();

  const {
    user: { getUserType },
    modelType: { changeModelType, getSelectedModelType },
  } = useStore(null);

  const userType: string = getUserType;
  const isHiddenGoBackButton = pathname.includes("/playground");
  const isHiddenModelSelection = pathname.includes("/results/");

  const tooltipHandler = (visible) => {
    if (isFirstRender && visible) {
      setIsTooltipShow(visible);
      setIsFirstRender(false);
    } else {
      setIsTooltipShow(false);
    }
  };

  useEffect(() => {
    if (!(isSubmitting && isRegenerating)) {
      setIsRegenerating(false);
    }
  }, [isSubmitting]);

  useEffect(() => {
    changeModelType("Titan Pro");
  }, [userType]);

  const getModelOptions = (userType: string, getSelectedModelType: string) => {
    const baseOptions = {
      Research: {
        label: (
          <div className={style.modelSelectorDropdownLabel}>
            <div className={style.modelSelectorDropdownLabelTitle}>
              {getSelectedModelType === "Research" && (
                <CheckOutlined
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "5px",
                  }}
                />
              )}
              Perplexity Deep Research
            </div>
            <div className={style.modelSelectorDropdownLabelDesc}>
              Use as a stand-alone tool to perform climate research. It reads
              hundreds of sources, and reasons through the material to
              autonomously deliver a comprehensive report.
            </div>
          </div>
        ),
        value: "Research",
        miniLabel: "Research",
      },
      Custom: {
        label: (
          <div className={style.modelSelectorDropdownLabel}>
            <div className={style.modelSelectorDropdownLabelTitle}>
              {getSelectedModelType === "Custom" && (
                <CheckOutlined
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "5px",
                  }}
                />
              )}
              Custom Model (Llama 4)
            </div>
            <div className={style.modelSelectorDropdownLabelDesc}>
              An in-house language model for secure use, trained on donor
              guidance, internal policies, and your selected information.
            </div>
          </div>
        ),
        value: "Custom",
        miniLabel: "Custom (Llama 4)",
        disabled: true, // TODO: enable when needed
      },
      "Titan Pro": {
        label: (
          <div className={style.modelSelectorDropdownLabel}>
            <div className={style.modelSelectorDropdownLabelTitle}>
              {getSelectedModelType === "Titan Pro" && (
                <CheckOutlined
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "5px",
                  }}
                />
              )}
              Titan Pro
            </div>
            <div className={style.modelSelectorDropdownLabelDesc}>
              Smartest language model available. Advanced reasoning. Responses
              may take longer than average.
            </div>
          </div>
        ),
        value: "Titan Pro",
        miniLabel: "Titan Pro",
      },
      "Titan Flash": {
        label: (
          <div className={style.modelSelectorDropdownLabel}>
            <div className={style.modelSelectorDropdownLabelTitle}>
              {getSelectedModelType === "Titan Flash" && (
                <CheckOutlined
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "5px",
                  }}
                />
              )}
              Titan Flash
            </div>
            <div className={style.modelSelectorDropdownLabelDesc}>
              Our powerful default model with fast responses. Great for
              reviewing resources and general queries.
            </div>
          </div>
        ),
        value: "Titan Flash",
        miniLabel: "Titan Flash",
      },
      close: {
        label: (
          <span
            style={{
              display: "flex",
              height: "100%",
              alignItems: "flex-end",
              justifyContent: "center",
            }}
          >
            <DownOutlined />
          </span>
        ),
        value: "close",
      },
    };

    const typeMappings: Record<string, string[]> = {
      basic: ["Titan Pro", "Titan Flash", "Custom"],
      premium: ["Titan Pro", "Titan Flash", "Custom"],
      enterprise: ["Titan Pro", "Titan Flash", "Custom"],
    };

    return (typeMappings[userType] || [])
      .map((model) => baseOptions[model])
      .concat(baseOptions.close);
  };

  const tooltipData = () => {
    return (
      <div style={{ paddingLeft: 11, paddingRight: 11, color: "#000000" }}>
        <p
          style={{
            margin: 0,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "30px",
            color: "#000000",
          }}
        >
          Important!
        </p>
        <p
          style={{
            marginTop: 4,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontSize: 14,
          }}
        >
          By selecting this button, the model will regenerate an alternative
          result to the previous one. The new result will be generated below the
          most recent output.
        </p>
        {/* <hr /> */}
        <div
          style={{
            marginTop: 12,
            padding: "12px 20px",
            marginLeft: -19,
            marginRight: -19,
            marginBottom: -6,
            border: "1px solid #E0E0E0",
          }}
        >
          <Button
            onClick={() => setIsTooltipShow(false)}
            style={{
              backgroundColor: "#00B840",
              width: 66,
              padding: " 6px 20px 10px 15px",
              height: 34,
              color: "#FFFFFF",
            }}
          >
            Got it
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      {isResult ? (
        <div
          style={customStyle && customStyle}
          className={style.footerButtonsDiv}
        >
          <div className={style.leftBtnContainer}>
            <Button
              loading={isLoadingSubmit}
              disabled={isLoadingSubmit || disableSubmit}
              onClick={() => handleGoNext()}
              htmlType="submit"
              className={style.nextButton}
            >
              {isResult ? "Next" : isSubmit ? "Next" : "Submit"}
            </Button>

            {!isLoadingRegenrate ? (
              <></>
            ) : (
              // <Tooltip open={isTooltipShow} onOpenChange={tooltipHandler} overlayInnerStyle={{width:294}} className={style.tooltipWrapper} color="#FFFF" title={tooltipData} >
              //   <img  className={style.reGenerateImg} onClick={() => {
              //   handleRegenrate();
              //   setGenerateText('Find below your regenerated narrative:');
              // }} src={constImages.RegenerateBtn} alt="" /> </Tooltip>
              <Button
                onClick={() => handleRegenrate()}
                className={style.reGenerate}
                loading={isLoadingRegenrate}
                disabled={isLoadingRegenrate}
              >
                Regenerate
              </Button>
            )}
          </div>
          <div className={style.btnDiv}>
            <div className={style.twoBtnDiv}>
              <GoBack
                isTooltip={true}
                onClickForFooterGo={() => {
                  handlegoback();
                  localStorage.setItem("isStopGeneration", String(true));
                }}
                isSubmitting={isSubmitting}
              />
              {!pathname.includes("/playground") && (
                <SaveQuit onClickForFooter={handleSaveAndQuit} />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          style={customStyle && customStyle}
          className={style.footerButtonsDiv}
        >
          <Form
            form={form}
            onFinish={withRegenerate ? handleRegenrate : handleSubmit}
          >
            {withRegenerate ? (
              <Button
                onClick={() => {
                  handleRegenrate();
                  setIsRegenerating(true);
                }}
                className={style.reGenerate}
                loading={isLoadingRegenrate}
                disabled={isLoadingRegenrate}
              >
                <RegenerateIcon /> Regenerate
              </Button>
            ) : (
              <div className={style.twoBtnDiv}>
                <Button
                  loading={isLoadingSubmit}
                  disabled={isLoadingSubmit || disableSubmit}
                  // onClick={() => handleSubmit()}
                  htmlType="submit"
                  className={style.nextButton}
                >
                  {isSubmit ? "Next" : "Submit"}
                </Button>
                {pathname.includes("/evaluate/") && type === "results" && (
                  <div
                    className={
                      isLoadingSubmit === true || disableSubmit === true
                        ? style.continueButtonDisabled
                        : style.continueButton
                    }
                    onClick={() => handleContinueInPlayground()}
                  >
                    Continue In Playground
                  </div>
                )}
                {isHiddenModelSelection !== true && (
                  <Select
                    defaultValue={userType === "basic" ? "Swift" : "Titan"}
                    style={{ width: "250px", textAlign: "start" }}
                    listHeight={420}
                    suffixIcon={<img src={down} alt={"down icon"} />}
                    onChange={(value) => changeModelType(value)}
                    value={getSelectedModelType}
                    optionLabelProp="miniLabel"
                    options={getModelOptions(userType, getSelectedModelType)}
                  />
                )}
              </div>
            )}
            {/* <Button
              className={style.quickNextButton}
              onClick={() => navigate(handleQuickNext)}
            >
              Quick next
            </Button> */}
          </Form>
          <div className={style.btnDiv}>
            <div className={style.twoBtnDiv}>
              {/*{isSubmitting ? (*/}
              {/*  <div*/}
              {/*    className={style.stopButton}*/}
              {/*    onClick={() => {*/}
              {/*      setIsSubmitting(false);*/}
              {/*      localStorage.setItem("isStopGeneration", String(true));*/}
              {/*    }}*/}
              {/*  >*/}
              {/*    Stop*/}
              {/*  </div>*/}
              {/*) : (*/}
              {/*  !isHiddenGoBackButton && (*/}
              {/*    <GoBack*/}
              {/*      isTooltip={isTooltip}*/}
              {/*      onClickForFooterGo={handlegoback}*/}
              {/*      isSubmitting={isSubmitting}*/}
              {/*    />*/}
              {/*  )*/}
              {/*)}*/}
              {!isHiddenGoBackButton && (
                <GoBack
                  isTooltip={true}
                  onClickForFooterGo={() => {
                    setIsSubmitting && setIsSubmitting(false);
                    localStorage.setItem("isStopGeneration", String(true));
                    handlegoback();
                  }}
                  // isSubmitting={isSubmitting}
                />
              )}
              {!pathname.includes("/playground") && (
                <SaveQuit
                  onClickForFooter={handleSaveAndQuit}
                  isSaving={isSaving}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default observer(FooterButton);
